import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Modal from "@/components/Modal";
import { Check, X, Scroll, Trophy, ArrowRight, Plus, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";

function OfflineAuctionModal({ open, onClose, onCreated }) {
  const [organizers, setOrganizers] = useState([]);
  const [form, setForm] = useState({
    organizer_id: "",
    name: "",
    number_of_teams: 4,
    venue: "",
    offline_amount_inr: 3000,
    offline_note: "",
    start_date: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/superadmin/organizers/list").then(({ data }) => setOrganizers(data));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.organizer_id) { toast.error("Pick an organizer"); return; }
    setBusy(true);
    try {
      await api.post("/superadmin/auctions/create-for-organizer", {
        organizer_id: form.organizer_id,
        name: form.name,
        number_of_teams: Number(form.number_of_teams) || 4,
        venue: form.venue || null,
        start_date: form.start_date || null,
        offline_amount_inr: Number(form.offline_amount_inr) || 0,
        offline_note: form.offline_note || "",
      });
      toast.success("Offline auction created on behalf of organizer");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Create auction (offline paid)" subtitle="SUPERADMIN" size="lg" testId="offline-auction-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="offline-auction-form">
        <div>
          <label className="label-mono block mb-2">Organizer (paid offline) *</label>
          <select required value={form.organizer_id} onChange={(e) => setForm({ ...form, organizer_id: e.target.value })} data-testid="offline-organizer-select" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none">
            <option value="">— Pick organizer —</option>
            {organizers.map((o) => (
              <option key={o.id} value={o.id}>{`${o.name || o.email} · ${o.email}`}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-mono block mb-2">Auction name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offline-auction-name" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="label-mono block mb-2">Venue</label>
            <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="label-mono block mb-2">Number of teams (2–30)</label>
            <input type="number" min={2} max={30} value={form.number_of_teams} onChange={(e) => setForm({ ...form, number_of_teams: e.target.value })} data-testid="offline-team-count" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="label-mono block mb-2">Start date</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
          </div>
        </div>

        <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-3">
          <div className="label-mono text-blue-700 flex items-center gap-1"><CurrencyInr size={14} /> OFFLINE PAYMENT DETAILS</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="label-mono block mb-2">Amount received (INR)</label>
              <input type="number" min={0} value={form.offline_amount_inr} onChange={(e) => setForm({ ...form, offline_amount_inr: e.target.value })} data-testid="offline-amount" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-mono block mb-2">Payment note</label>
              <input value={form.offline_note} onChange={(e) => setForm({ ...form, offline_note: e.target.value })} placeholder="e.g. Cash received 2026-02-15 · Ref #142" data-testid="offline-note" className="w-full bg-white border border-gray-200 rounded px-3 py-2 focus:border-blue-500 outline-none" />
            </div>
          </div>
          <p className="text-xs text-gray-600">Recorded for revenue reporting. Razorpay will not be charged. Auction defaults (budget, tiers) can be edited later from Settings.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <button type="button" onClick={onClose} className="btn-ghost !px-5 !py-2 text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary !px-5 !py-2 text-xs" data-testid="submit-offline-auction">
            {busy ? "Creating…" : "Create Auction"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AllAuctionsBrowser() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOffline, setShowOffline] = useState(false);
  const reload = () => {
    setLoading(true);
    api.get("/superadmin/auctions")
      .then(({ data }) => setAuctions(data))
      .catch(() => setAuctions([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);
  return (
    <section data-testid="superadmin-all-auctions">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-blue-600" />
          <h2 className="font-display uppercase text-3xl">All Auctions ({auctions.length})</h2>
        </div>
        <button onClick={() => setShowOffline(true)} data-testid="create-offline-auction-button" className="btn-primary !px-4 !py-2 text-xs flex items-center gap-2">
          <Plus size={12} /> Create for organizer (offline)
        </button>
      </div>
      {loading ? (
        <div className="label-mono py-8 text-center">Loading auctions…</div>
      ) : auctions.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500">No auctions yet.</div>
      ) : (
        <div className="bg-slate-50 border border-gray-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-left">
              <tr className="text-xs">
                <th className="px-4 py-2 label-mono">Auction</th>
                <th className="px-4 py-2 label-mono">Organizer</th>
                <th className="px-4 py-2 label-mono">Status</th>
                <th className="px-4 py-2 label-mono text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {auctions.map((a) => (
                <tr key={a.id} className="border-t border-gray-100" data-testid={`sa-auction-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{a.name}</div>
                    <div className="label-mono">{a.venue || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{a.organizer_name || a.organizer_email || "—"}</div>
                    <div className="text-xs text-gray-500">{a.organizer_email || ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded border ${a.status === "live" ? "bg-red-50 text-red-700 border-red-200" : a.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-700 border-gray-200"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/auction/${a.id}/manage`} className="btn-primary text-xs inline-flex items-center gap-2 !px-3 !py-1.5" data-testid={`sa-manage-${a.id}`}>
                      Manage <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <OfflineAuctionModal open={showOffline} onClose={() => setShowOffline(false)} onCreated={reload} />
    </section>
  );
}

function AuditLogViewer() {
  const [auctions, setAuctions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/superadmin/auctions")
      .then(({ data }) => {
        setAuctions(data);
        if (data.length > 0) setSelectedId(data[0].id);
      })
      .catch(() => setAuctions([]));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    api.get(`/auctions/${selectedId}/audit-logs`)
      .then(({ data }) => setLogs(data))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [selectedId]);

  return (
    <section data-testid="audit-log-viewer">
      <div className="flex items-center gap-2 mb-4">
        <Scroll size={20} className="text-emerald-600" />
        <h2 className="font-display uppercase text-3xl">Audit log</h2>
      </div>

      <div className="mb-3">
        <label className="label-mono block mb-2">Select Tournament / Auction</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full md:w-96 bg-white border border-gray-200 rounded px-3 py-2 focus:border-emerald-500 outline-none"
          data-testid="audit-auction-select"
        >
          {auctions.length === 0 && <option value="">No auctions available</option>}
          {auctions.map((a) => (
            <option key={a.id} value={a.id}>
              {`${a.name} · ${a.status} · ${a.organizer_name || a.organizer_email || "—"}`}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="label-mono py-10 text-center">Loading logs…</div>
      ) : logs.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500">
          No audit entries for this auction.
        </div>
      ) : (
        <div className="bg-slate-50 border border-gray-200 rounded overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-left sticky top-0">
              <tr className="text-xs">
                <th className="px-4 py-2 label-mono">When</th>
                <th className="px-4 py-2 label-mono">Who</th>
                <th className="px-4 py-2 label-mono">Action</th>
                <th className="px-4 py-2 label-mono">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-gray-100" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">
                    {new Date(l.at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="font-semibold">{l.actor_name || "—"}</div>
                    <div className="label-mono">{l.actor_role || "—"}</div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="text-xs font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 rounded px-2 py-1">
                      {l.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500 max-w-md">
                    <code className="font-mono break-all">{l.details ? JSON.stringify(l.details) : "—"}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function SuperadminConsole() {
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([api.get("/superadmin/stats"), api.get("/superadmin/organizers")]);
      setStats(s.data); setOrgs(o.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try { await api.patch(`/superadmin/organizers/${u.id}`, { active: !u.active }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="label-mono py-24 text-center">Loading…</div>;

  return (
    <div className="page-dark -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 -my-6 sm:-my-8">
    <div className="space-y-8" data-testid="superadmin-console">
      <div>
        <div className="label-mono mb-2">PLATFORM</div>
        <h1 className="font-display uppercase text-5xl">Superadmin console</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ["Auctions", stats.total_auctions],
            ["Live now", stats.live_auctions, "text-emerald-600"],
            ["Organizers", stats.total_organizers],
            ["Players", stats.total_players],
            ["Teams", stats.total_teams],
          ].map(([l, v, acc]) => (
            <div key={l} className="bg-white border border-gray-200 rounded p-4">
              <div className="label-mono">{l}</div>
              <div className={`stat-number ${acc || ""}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <AllAuctionsBrowser />

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={20} className="text-blue-600" />
          <h2 className="font-display uppercase text-3xl">Tournament Organizers ({orgs.length})</h2>
        </div>
        {orgs.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded p-10 text-center text-gray-500">No organizers yet.</div>
        ) : (
          <div className="bg-slate-50 border border-gray-200 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white text-left">
                <tr className="text-xs">
                  <th className="px-4 py-2 label-mono">Organizer</th>
                  <th className="px-4 py-2 label-mono">Contact</th>
                  <th className="px-4 py-2 label-mono">Auctions</th>
                  <th className="px-4 py-2 label-mono">Status</th>
                  <th className="px-4 py-2 label-mono">Action</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100" data-testid={`org-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{u.name}</div>
                      <div className="label-mono">{u.organization || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{u.email}</div>
                      <div className="text-xs text-gray-500">{u.mobile || "—"}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{u.auction_count}</td>
                    <td className="px-4 py-3">
                      {u.active !== false ? <span className="text-emerald-600 text-xs">Active</span> : <span className="text-red-600 text-xs">Disabled</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggle(u)} className="btn-ghost text-xs !px-3 !py-1 flex items-center gap-1" data-testid={`toggle-org-${u.id}`}>
                        {u.active !== false ? <><X size={12} /> Disable</> : <><Check size={12} /> Enable</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AuditLogViewer />
    </div>
    </div>
  );
}
